import React, { useState, useEffect, useContext, useRef } from 'react';
import { InlineToast } from '../../interactives/Notifications/components/InlineToast';
import { InlineCallout } from '../../interactives/Notifications/components/InlineCallout';
import { NotificationContext } from '../../interactives/Notifications/context/NotificationContext';

export interface AlertConfig {
  kind?: 'error' | 'warning' | 'success' | 'info';
  title: string;
  subtitle?: string;
  type?: 'toast' | 'callout';
}

interface PageCardProps {
  heading?: string;
  className?: string;
  children: React.ReactNode;
  showHeader?: boolean;
  showContent?: boolean;
  showFooter?: boolean;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  footerDescription?: string;
  footerButton?: React.ReactNode;
  width?: number;
  shadow?: boolean;
  padding?: boolean;
  showDividers?: boolean;
  headerPadding?: string;
  contentPadding?: string;
  footerPadding?: string;
  
  // Alert convenience prop - inline notification on mobile
  alert?: boolean | string | AlertConfig;
  
  // Alert close callback
  onAlertClose?: () => void;
}

export function PageCard({
  heading,
  className = '',
  children,
  showHeader = true,
  showContent = true,
  showFooter = false,
  headerContent,
  footerContent,
  footerDescription,
  footerButton,
  width = 480,
  shadow = false,
  padding = false,
  showDividers = false,
  headerPadding = '1rem',
  contentPadding,
  footerPadding = '1rem',
  alert,
  onAlertClose
}: PageCardProps) {
  // Initialize isMobile correctly on mount
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 450px)').matches;
    }
    return false;
  });
  
  // Track alert changes to force re-render with unique key
  const alertKeyRef = React.useRef(0);
  const prevAlertRef = React.useRef<string>('');
  
  // Track desktop notification ID for cleanup
  const alertIdRef = useRef<string | null>(null);
  const alertStringRef = useRef<string>('');
  
  // Get notification context for desktop toasts
  const notificationContext = useContext(NotificationContext);
  
  // Generate alert config from convenience prop
  const alertConfig = React.useMemo((): AlertConfig | null => {
    if (!alert) return null;
    
    if (typeof alert === 'boolean') {
      return { kind: 'error', title: 'An error occurred', type: 'toast' };
    }
    if (typeof alert === 'string') {
      return { kind: 'error', title: alert, type: 'toast' };
    }
    return { kind: 'error', type: 'toast', ...alert };
  }, [alert]);
  
  // Update alert key when alert changes
  useEffect(() => {
    const currentAlert = alertConfig ? JSON.stringify(alertConfig) : '';
    if (currentAlert && currentAlert !== prevAlertRef.current) {
      alertKeyRef.current += 1;
      prevAlertRef.current = currentAlert;
    } else if (!currentAlert) {
      prevAlertRef.current = '';
    }
  }, [alertConfig]);
  
  // Media query to detect mobile breakpoint
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 450px)');
    setIsMobile(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => {
      const newIsMobile = e.matches;
      setIsMobile(newIsMobile);
      
      // When switching from mobile to desktop or vice versa, dismiss desktop toast
      if (newIsMobile && alertIdRef.current && notificationContext) {
        notificationContext.dismiss(alertIdRef.current);
        alertIdRef.current = null;
        alertStringRef.current = '';
      }
    };
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [notificationContext]);
  
  // Desktop: Show toast notification (ONLY when NOT mobile and screen is > 450px)
  useEffect(() => {
    // Double-check screen size to prevent mobile toasts
    const isCurrentlyMobile = window.matchMedia('(max-width: 450px)').matches;
    
    // Create a stable string representation of alert for comparison
    const currentAlertString = alertConfig ? JSON.stringify(alertConfig) : '';
    
    // ONLY show desktop toast if ALL of these are true:
    // 1. isMobile state is false
    // 2. Current window size check confirms NOT mobile
    // 3. Alert exists
    // 4. Notification context is available
    // 5. Alert has changed
    if (!isMobile && !isCurrentlyMobile && alertConfig && notificationContext && currentAlertString !== alertStringRef.current) {
      // Dismiss previous alert if exists
      if (alertIdRef.current) {
        notificationContext.dismiss(alertIdRef.current);
      }
      
      // Show new alert as corner toast or callout
      alertIdRef.current = notificationContext.notify({
        type: alertConfig.type || 'toast',
        kind: alertConfig.kind || 'error',
        title: alertConfig.title,
        subtitle: alertConfig.subtitle,
        duration: alertConfig.kind === 'error' ? undefined : 5000
      });
      
      alertStringRef.current = currentAlertString;
    }
    
    // Cleanup when alert is cleared
    if (!alertConfig && alertIdRef.current && notificationContext) {
      notificationContext.dismiss(alertIdRef.current);
      alertIdRef.current = null;
      alertStringRef.current = '';
    }
  }, [isMobile, alertConfig, notificationContext]);
  
  const fullClassName = `core-page-card-container ${className}`.trim();
  
  // Only set max-width via CSS custom property
  const style: React.CSSProperties = {
    ['--card-max-width' as any]: `${width}px`
  };
  
  // Apply shadow if requested
  if (shadow) {
    style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
  }

  return (
    <div className={fullClassName} style={style}>
      {showHeader && (heading || headerContent) && (
        <>
          <div className="core-page-card-header" style={{
            padding: headerPadding
          }}>
            {heading && <h2 className="core-page-card-heading">{heading}</h2>}
            {headerContent}
          </div>
          {showDividers && showContent && (
            <div className="core-page-card-divider" />
          )}
        </>
      )}
      
      {/* Mobile inline alert - appears below header (only when IS mobile) */}
      {alertConfig && isMobile && (
        <div className="core-page-card-alert" key={`alert-${alertKeyRef.current}`}>
          {alertConfig.type === 'callout' ? (
            <InlineCallout
              id="page-card-inline-alert"
              kind={alertConfig.kind || 'error'}
              title={alertConfig.title}
              subtitle={alertConfig.subtitle}
              hideCloseButton={false}
              onClose={onAlertClose}
              timestamp={Date.now()}
            />
          ) : (
            <InlineToast
              id="page-card-inline-alert"
              kind={alertConfig.kind || 'error'}
              title={alertConfig.title}
              subtitle={alertConfig.subtitle}
              hideCloseButton={false}
              lowContrast={false}
              variant="translucent"
              onClose={onAlertClose}
              timestamp={Date.now()}
            />
          )}
        </div>
      )}
      
      {showContent && (
        <div className="core-page-card-content" style={{
          padding: contentPadding !== undefined ? contentPadding : (padding ? '1rem' : 0),
          flex: 1,
          overflowY: 'auto',
          minHeight: 'fit-content'
        }}>
          {children}
        </div>
      )}
      
      {showFooter && (footerContent || footerDescription || footerButton) && (
        <>
          {showDividers && (showContent || showHeader) && (
            <div className="core-page-card-divider" />
          )}
          <div className="core-page-card-footer" style={{
            padding: footerPadding,
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {footerContent ? (
              footerContent
            ) : (
              <>
                {footerDescription && (
                  <div className="core-page-card-footer-description">
                    {footerDescription}
                  </div>
                )}
                {footerButton && (
                  <div className="core-page-card-footer-button">
                    {footerButton}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default PageCard;
